import { Col, Row } from 'antd';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { AmountUnit } from '../../../../../api/types';
import RadioGroupField from '../../../../../components/fields/RadioGroupField';
import SelectField from '../../../../../components/fields/SelectField';
import FormFields from '../../../../../themes/admin/Fields/FormFields';
import { useFinanceProductDetailContext } from '../../FinanceProductDetailContext';
import { CommonType } from '../../shared/typings';
import useFinanceProductOptions from '../../shared/useFinanceProductOptions';
import { type FinanceProductFormValues } from '../ProductDetailsForm';
import YesNoField from './YesNoField';

const colSpan = { lg: 8, xs: 24 };

const Balloon = ({ disabled = false }: { disabled?: boolean }) => {
    const { t } = useTranslation(['financeProductDetails']);

    const { values } = useFormikContext<FinanceProductFormValues>();

    const { bank } = useFinanceProductDetailContext();
    const { balloonBasedOnOptions, commonTypeOptions, amountUnitOptions } = useFinanceProductOptions(
        bank?.module?.companyId
    );

    const { balloon } = values;

    const isRange = balloon?.type === CommonType.Range;
    const min = balloon?.min ?? 0;
    const max = balloon?.max ?? 0;

    return (
        <Row gutter={[16, 16]}>
            <Col span={24}>
                <RadioGroupField
                    {...t('financeProductDetails:fields.balloonType', { returnObjects: true })}
                    disabled={disabled}
                    name="balloon.type"
                    options={commonTypeOptions}
                    required
                />
            </Col>
            {isRange && (
                <>
                    <Col {...colSpan}>
                        <FormFields.InputNumberField
                            {...t('financeProductDetails:fields.balloonMin', {
                                returnObjects: true,
                            })}
                            addonAfter={
                                <SelectField
                                    itemProps={{ noStyle: true }}
                                    name="balloon.minUnit"
                                    options={amountUnitOptions}
                                />
                            }
                            disabled={disabled}
                            max={balloon?.minUnit === AmountUnit.Percentage ? 100 : Number.MAX_SAFE_INTEGER}
                            min={0}
                            name="balloon.min"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.InputNumberField
                            {...t('financeProductDetails:fields.balloonMax', {
                                returnObjects: true,
                            })}
                            addonAfter={
                                <SelectField
                                    itemProps={{ noStyle: true }}
                                    name="balloon.maxUnit"
                                    options={amountUnitOptions}
                                />
                            }
                            disabled={disabled}
                            max={balloon?.maxUnit === AmountUnit.Percentage ? 100 : Number.MAX_SAFE_INTEGER}
                            min={0}
                            name="balloon.max"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.InputNumberField
                            {...t('financeProductDetails:fields.balloonStep', {
                                returnObjects: true,
                            })}
                            addonAfter={
                                <SelectField
                                    itemProps={{ noStyle: true }}
                                    name="balloon.stepUnit"
                                    options={amountUnitOptions}
                                />
                            }
                            disabled={min >= max || disabled}
                            max={balloon?.stepUnit === AmountUnit.Percentage ? 100 : Number.MAX_SAFE_INTEGER}
                            min={0}
                            name="balloon.step"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.InputNumberField
                            {...t('financeProductDetails:fields.balloonDefault', {
                                returnObjects: true,
                            })}
                            addonAfter={
                                <SelectField
                                    itemProps={{ noStyle: true }}
                                    name="balloon.unit"
                                    options={amountUnitOptions}
                                />
                            }
                            disabled={disabled}
                            max={balloon?.unit === AmountUnit.Percentage ? 100 : Number.MAX_SAFE_INTEGER}
                            min={0}
                            name="balloon.default"
                            required
                        />
                    </Col>
                </>
            )}
            <Col {...colSpan}>
                <FormFields.SelectField
                    {...t('financeProductDetails:fields.balloonBasedOn', { returnObjects: true })}
                    disabled={disabled}
                    name="balloon.basedOn"
                    options={balloonBasedOnOptions}
                    required
                    showSearch
                />
            </Col>
            <Col {...colSpan}>
                <YesNoField
                    {...t('financeProductDetails:fields.balloonViewable', { returnObjects: true })}
                    disabled={disabled}
                    name="balloon.isViewable"
                />
            </Col>
            {isRange && (
                <Col {...colSpan}>
                    <YesNoField
                        {...t('financeProductDetails:fields.balloonEditable', { returnObjects: true })}
                        disabled={!balloon?.isViewable || disabled}
                        name="balloon.isEditable"
                    />
                </Col>
            )}
        </Row>
    );
};

export default Balloon;
